import { useEffect, useState, useMemo } from 'react';
import { useStore } from '../context/StoreContext';
import { Icon } from '../shared/ui/Icon';
import { formatPrice, safeImage, categoryName, getProductImages } from '../entities/product/helpers';
import { ProductStrip } from '../widgets/ProductStrip';
import { LOW_STOCK_THRESHOLD } from '../shared/config/constants';
import { ProductImage } from '../entities/product/ProductImage';

export function ProductDetailPage() {
  const {
    selectedProduct, products, settings,
    go, browseCategory, addToCart, buyNow, favorites, toggleFavorite, openAuth,
  } = useStore();

  const p = selectedProduct;
  const title = p?.title || p?.name || 'Product Details';
  const relatedProducts = useMemo(() => {
    if (!p) return [];
    return products
      .filter((item) => item.id !== p.id && (item.category === p.category || !p.category))
      .slice(0, 4);
  }, [p, products]);

  const allImages = useMemo(() => (p ? getProductImages(p) : []), [p]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedStorage, setSelectedStorage] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);

  useEffect(() => {
    setActiveImageIndex(0);
    setSelectedColor('');
    setSelectedStorage('');
    setQuantity(1);
    setActiveTab('description');
  }, [p?.id]);
  useEffect(() => {
    if (!zoomOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setZoomOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [zoomOpen]);
  if (!p) {
    return (
      <div className="empty-state">
        <h2>Product not found</h2>
        <p>The product you are looking for may have been moved or removed.</p>
        <button className="button button-primary" onClick={() => browseCategory()}>
          Explore Catalog <Icon>↗</Icon>
        </button>
      </div>
    );
  }

  const activeImg = allImages[activeImageIndex] || safeImage(p);
  const hasStockData = p.stock !== null && p.stock !== undefined;
  const stock = Number(p.stock);
  const isInStock = !hasStockData || stock > 0;
  const isLowStock = hasStockData && stock > 0 && stock <= LOW_STOCK_THRESHOLD;
  const colors = p.variants?.colors || p.colorVariants || p.colors || [];
  const storageOptions = p.variants?.storage || p.storageVariants || p.storageOptions || [];
  const productVariants = p.variants && Array.isArray(p.variants)
    ? {
      colors: p.variants.filter((variant) => variant.type === 'color'),
      storage: p.variants.filter((variant) => variant.type === 'storage'),
    }
    : {
      colors: Array.isArray(colors) ? colors : [],
      storage: Array.isArray(storageOptions) ? storageOptions : [],
    };
  const reviews = Array.isArray(p.reviews) ? p.reviews : [];
  const trustItems = settings?.trustItems || settings?.trustStrip || [];
  const reviewCount = Number(p.reviewCount || reviews.length);
  const rating = Number(p.rating || 0);
  const isFavorite = favorites.includes(String(p.id));
  const isDemoProduct = String(p.id).startsWith('demo-');
  const price = Number(p.salePrice) > 0 && Number(p.salePrice) < Number(p.price)
    ? Number(p.salePrice)
    : Number(p.price || 0);
  const oldPrice = Number(p.oldPrice ?? p.price ?? 0);
  const discount = oldPrice > price ? Math.round(((oldPrice - price) / oldPrice) * 100) : 0;
  const specs = Array.isArray(p.specs)
    ? p.specs
    : p.specs && typeof p.specs === 'object'
      ? Object.entries(p.specs).map(([label, value]) => ({ label, value }))
      : [];
  const fallbackTrustItems = [
    { id: 'delivery', icon: '◷', title: 'Fast delivery', description: 'Tbilisi delivery available' },
    { id: 'returns', icon: '↺', title: 'Easy returns', description: 'Contact us within 14 days' },
    { id: 'warranty', icon: '✓', title: 'Warranty support', description: 'Manufacturer warranty included' },
  ];

  async function handleAddToCart() {
    setAddingToCart(true);
    try {
      await addToCart(p, quantity);
    } finally {
      setAddingToCart(false);
    }
  }

  return (
    <div className="product-detail-page">
      <div className="breadcrumb">
        <button onClick={() => go('home')}>Home</button>
        <i>/</i>
        <button onClick={() => browseCategory(p.category)}>{categoryName(p.category)}</button>
        <i>/</i>
        <span>{title}</span>
      </div>

      <div className="product-detail">
        {/* ── Gallery ── */}
        <div className="detail-gallery">
        <div className="detail-main-image" role="button" tabIndex={0} aria-label={`Zoom image of ${title}`} onClick={() => setZoomOpen(true)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setZoomOpen(true); } }}>
          <ProductImage src={activeImg} alt={title} title={title} category={p.category} className="detail-product-image" priority />
          <span className="image-zoom-hint">Click to zoom</span>
          </div>
          {allImages.length > 1 && (
            <div className="detail-thumbnails">
              {allImages.map((img, idx) => (
                <button
                  type="button"
                  key={idx}
                  className={`thumbnail-btn ${activeImageIndex === idx ? 'active' : ''}`}
                  onClick={() => setActiveImageIndex(idx)}
                >
                  <img src={img} alt={`View ${idx + 1} of ${title}`} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Copy & Buy Controls ── */}
        <div className="detail-copy">
          <p className="eyebrow">{p.brand || categoryName(p.category)}</p>
          {isDemoProduct && (
            <p className="demo-detail-note">Fictional demo listing · sample price, not a real offer</p>
          )}
          <h1>{title}</h1>
          {reviewCount > 0 && rating > 0 ? (
            <div className="detail-rating">
              <span aria-label={`${rating.toFixed(1)} out of 5 stars`}>★★★★★</span> {rating.toFixed(1)}
              <i>·</i>
              <button
                type="button"
                onClick={() => {
                  setActiveTab('reviews');
                  document.getElementById('product-tab-reviews')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
              >
                {reviewCount} reviews
              </button>
            </div>
          ) : (
            <div className="detail-rating-empty">
              <span>No reviews yet</span>
              <button type="button" onClick={() => {
                setActiveTab('reviews');
                document.getElementById('product-tab-reviews')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}>Be the first to review</button>
            </div>
          )}
          <div className="detail-price">
            <strong>{formatPrice(price)}</strong>
            {discount > 0 && <del>{formatPrice(oldPrice)}</del>}
            {discount > 0 && <span className="sale-tag">-{discount}%</span>}
          </div>
          <p className="detail-description">
            {p.description}
          </p>

          {hasStockData && (
            <div className={`stock-note ${!isInStock ? 'stock-empty' : isLowStock ? 'stock-warn' : ''}`}>
              <i />
              {isInStock
                ? isLowStock
                  ? `Only ${stock} left`
                  : 'In stock'
                : 'Out of stock'}
            </div>
          )}

          {!!productVariants.colors.length && (
            <div className="variant-selector">
              <b>Color{selectedColor ? `: ${selectedColor}` : ''}</b>
              <div className="variant-swatches">
                {productVariants.colors.map((color, index) => {
                  const name = typeof color === 'string' ? color : color.name || color.value || `Color ${index + 1}`;
                  const colorValue = typeof color === 'string' ? color : color.hex || color.value || color.name;
                  return (
                    <button
                      key={`${name}-${index}`}
                      type="button"
                      className={`color-swatch ${selectedColor === name ? 'selected' : ''}`}
                      aria-label={name}
                      title={name}
                      aria-pressed={selectedColor === name}
                      style={{ '--swatch-color': colorValue }}
                      onClick={() => setSelectedColor(name)}
                    />
                  );
                })}
              </div>
            </div>
          )}
          {!!productVariants.storage.length && (
            <div className="variant-selector">
              <b>Storage</b>
              <div className="variant-chips">
                {productVariants.storage.map((option, index) => {
                  const name = typeof option === 'string' ? option : option.name || option.value || `Option ${index + 1}`;
                  return (
                    <button
                      key={`${name}-${index}`}
                      type="button"
                      className={selectedStorage === name ? 'selected' : ''}
                      aria-pressed={selectedStorage === name}
                      onClick={() => setSelectedStorage(name)}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="detail-actions">
            <div className="detail-quantity" aria-label="Quantity">
              <button type="button" aria-label="Decrease quantity" disabled={quantity <= 1} onClick={() => setQuantity((current) => Math.max(1, current - 1))}>−</button>
              <span aria-live="polite">{quantity}</span>
              <button type="button" aria-label="Increase quantity" onClick={() => setQuantity((current) => current + 1)}>+</button>
            </div>
            <button
              className="button button-primary"
              disabled={!isInStock}
              onClick={handleAddToCart}
            >
              {addingToCart ? 'Adding…' : 'Add to Cart'} <Icon>＋</Icon>
            </button>
            <button
              className="button button-outline buy-now"
              disabled={!isInStock}
              onClick={() => buyNow(p, quantity)}
            >
              Buy Now <Icon>→</Icon>
            </button>
            <button
              className="button button-outline favorite-detail"
              aria-label={isFavorite ? 'Remove from favorites' : 'Save to favorites'}
              aria-pressed={isFavorite}
              onClick={() => toggleFavorite(p)}
            >
              <Icon>{isFavorite ? '♥' : '♡'}</Icon>
            </button>
          </div>

          <div className="detail-service-info">
            <p><b>Delivery estimate</b><span>Usually delivered in 1–3 business days in Tbilisi.</span></p>
            <p><b>Returns & warranty</b><span>14-day return support and manufacturer warranty.</span></p>
          </div>

          {p.ganvadebaLink && (
            <div className="installment-banner">
              <span>◷</span>
              <div>
                <b>Online Installment Available (Ganvadeba)</b>
                <small>Apply for monthly installments directly.</small>
              </div>
              <a href={p.ganvadebaLink} target="_blank" rel="noopener noreferrer" className="text-link">
                Apply ↗
              </a>
            </div>
          )}

          {(trustItems.length > 0 ? trustItems : fallbackTrustItems).length > 0 && (
            <div className="detail-perks">
              {(trustItems.length > 0 ? trustItems : fallbackTrustItems).map((item) => (
                <div key={item.id || item.title}>
                  <span>{item.icon ? <Icon>{item.icon}</Icon> : null}</span>
                  <p><b>{item.title}</b>{item.description && <small>{item.description}</small>}</p>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>

      <section className="product-tabs" aria-label="Product information">
        <div className="product-tab-list" role="tablist" aria-label="Product details">
          {[
            ['description', 'Description'],
            ['specifications', 'Specifications'],
            ['reviews', `Reviews (${reviewCount})`],
          ].map(([tab, label]) => (
            <button
              key={tab}
              type="button"
              role="tab"
              id={`product-tab-${tab}`}
              aria-selected={activeTab === tab}
              aria-controls={`product-panel-${tab}`}
              className={activeTab === tab ? 'active' : ''}
              onClick={() => setActiveTab(tab)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="product-tab-panel" role="tabpanel" id={`product-panel-${activeTab}`} aria-labelledby={`product-tab-${activeTab}`}>
          {activeTab === 'description' && (
            <p>{p.description || 'Thoughtfully designed technology, made for high reliability and everyday life.'}</p>
          )}
          {activeTab === 'specifications' && (
            specs.length ? (
              <div className="spec-table">
                {specs.map((spec, idx) => (
                  <div className="spec-row" key={idx}><span>{spec.label || `Specification ${idx + 1}`}</span><b>{spec.value}</b></div>
                ))}
              </div>
            ) : <p>Specifications are not available for this product.</p>
          )}
          {activeTab === 'reviews' && (
            reviews.length ? reviews.map((review, idx) => (
              <article className="product-review" key={review.id || idx}>
                <b>{review.name || review.userName || 'Verified customer'}</b>
                <span>{'★'.repeat(Math.min(5, Math.max(0, Number(review.rating || 0))))}</span>
                <p>{review.comment || review.text}</p>
              </article>
            )) : (
              <div className="first-review-cta">
                <p>No reviews yet. Be the first to share your experience.</p>
                <button className="button button-outline" type="button" onClick={() => openAuth('login')}>Sign in to write the first review <Icon>→</Icon></button>
              </div>
            )
          )}
        </div>
      </section>

      {zoomOpen && (
        <div className="image-lightbox" role="dialog" aria-modal="true" aria-label={`Image of ${title}`} onClick={() => setZoomOpen(false)}>
          <button type="button" aria-label="Close image zoom" onClick={() => setZoomOpen(false)}>×</button>
          {activeImg
            ? <img src={activeImg} alt={title} onClick={(event) => event.stopPropagation()} />
            : <div className="product-fallback-placeholder" role="img" aria-label={title}>{title}</div>}
        </div>
      )}

      {relatedProducts.length > 0 && (
        <ProductStrip items={relatedProducts} title="You may also like" />
      )}
    </div>
  );
}
